import React, { useEffect, useState } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import styled from 'styled-components';
import findValueDeep from 'deepdash/findValueDeep';
import { Button, PageLoader } from '@sumtotal/react-foundation';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import AppMenuHeader from '../../topnav/components/AppMenuHeader';
import getSitemap from '../../topnav/services/sitemapService';
import { GetProcessedNodeUrl, ProcessTreeNodes } from '../../../utils/urlHelper';
import getUserProfile from '../../topnav/services/personService';
import authService from '../../../authentication/AuthorizeService';
import { getSearchTypes } from '../../topnav/services/searchService';
import useUserContext from '../../hooks/useUserContext';
import ExpandedControlPanelContainer from '../../sidebar/components/ExpandedControlPanelContainer';
import EmulationStrip from './EmulationStrip';
import useHeaderContext from '../../hooks/useHeaderContext';
import { IntegrationElement } from '../../../utils/IntegrationConstants';
import CheckIntegrationSetting from '../../../utils/integrationHelper';
import useIntegrationContext from '../../hooks/useIntegrationContext';
import getMaxDepthOfTree from '../../../utils/treeHelper';

const HeaderSection = styled.div`
  height: '3.25rem';
`;

const IntegrationHomeBtn = styled(Button)`
  float: left;
`;

const coreNodes = ['self_personaldetails', 'Profile', 'menu_signout', 'self_signin'];

function updateExitEmulationUrl(profileData) {
  const { url } = profileData.filter((node) => node.nodeKey === 'menu_signout')[0];
  return url.replace('$HOST_NAME', window.location.host);
}

function SitemapProvider(props) {
  const { emulatedUser = defaultProps.emulatedUser } = props;
  checkPropTypes(SitemapProvider.propTypes, props, 'prop', SitemapProvider.name);
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);

  const [menusData, setMenusData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState({});
  const [profileMenus, setProfileMenus] = useState([]);
  const [helpMenu, setHelpMenu] = useState(null);
  const [myGuideMenu, setMyGuideMenu] = useState(null);
  const [notificationsMenu, setNotificationsMenu] = useState(null);
  const [shoppingCartMenu, setShoppingCartMenu] = useState(null);
  const [homeUrl, setHomeUrl] = useState(null);
  const { dispatch } = useUserContext();
  const [domainSwitchNode, setDomainSwitchNode] = useState(null);
  const [controlPanelItems, setcontrolPanelItems] = useState({});
  const [searchTypes, setSearchTypes] = useState([]);
  const [searchUrl, setSearchUrl] = useState(null);
  const [exitEmulationUrl, setExitEmulationUrl] = useState();
  const [coreReady, setCoreReady] = useState(false);
  const { headerContext } = useHeaderContext();
  const [selectedMenuKey, setSelectedMenuKey] = useState('');
  const { integrationContext } = useIntegrationContext();
  const { refreshSitemap } = queryString.parse(window.location.search, { parseBooleans: true });

  useEffect(() => {
    Promise.all([
      getSitemap(refreshSitemap),
      getUserProfile(),
      getSearchTypes(),
      authService.getUserProfile()
    ])
      .then((results) => {
        const data = results[0].sitemap;
        const termsForUse =
          results[0].termsAndConditionsLink && results[0].termsAndConditionsLink != null
            ? results[0].termsAndConditionsLink
            : null;
        const availableControlPanelNodes = updateControlPanelMenus(
          data.nodes,
          results[0].controlPanelMenuItems
        );
        const controlPanelNodes =
          availableControlPanelNodes != null ? availableControlPanelNodes : null;
        const controlPanelData = { Nodes: controlPanelNodes, Footer: termsForUse };
        setcontrolPanelItems(controlPanelData);
        // set exit emulation url
        if (emulatedUser && emulatedUser !== '') {
          const url = updateExitEmulationUrl(data.profileMenus);
          setExitEmulationUrl(url);
        }
        const userObject = results[1];
        if (userObject.mustAnswerSecurityQuestions) {
          userObject.UserJustAnsweredSecurityQuestion = !userObject.mustAnswerSecurityQuestions;
        } else {
          userObject.UserJustAnsweredSecurityQuestion = userObject.mustAnswerSecurityQuestions;
        }
        const landingDashBoardurl = GetProcessedNodeUrl(results[0].landingWebPart);
        setHomeUrl(landingDashBoardurl);
        setProfileMenus(data.profileMenus);
        setUserData(results[1]);
        if (results[2] && results[2].searchTypes) setSearchTypes(results[2].searchTypes);
        const accessibleRcoreRoutes = ProcessTreeNodes(data.nodes);
        setCurrentUser(
          results[1],
          landingDashBoardurl,
          results[3],
          accessibleRcoreRoutes,
          results[0].routeUrls
        );
        filterMenus(data, results[0].quickLinks);
        setErrorMessage('');
        setIsLoaded(true);
        return null;
      })
      .catch((errorResponse) => {
        setErrorMessage(errorResponse !== undefined ? errorResponse.data : '');
        setIsLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoaded && emulatedUser && emulatedUser !== '') {
      const focusElement = document.getElementById('exitEmulationBtn');
      if (focusElement)
        setTimeout(() => {
          focusElement.focus();
        }, 1000);
    }
  }, [isLoaded, emulatedUser]);

  const isEmulatedUser = emulatedUser !== '' && emulatedUser;
  // setting user details in UserContext
  function setCurrentUser(
    userDetails,
    homePageUrl,
    userDetailsAuthService,
    accessibleRcoreRoutes,
    securedRoutes
  ) {
    dispatch({
      type: 'USER_INFO',
      user: {
        name: userDetails.personInfo.fullName,
        userId: userDetails.personInfo.personNumber,
        personPK: userDetails.personInfo.personPK,
        image: userDetails.personInfo.imagePath,
        jobTitle: userDetails.jobTitle,
        mustAnswerSecurityQuestions: userDetails.mustAnswerSecurityQuestions,
        culture: userDetailsAuthService.culture,
        dateFormat: userDetailsAuthService.dateformat,
        timeFormat: userDetailsAuthService.timeformat,
        userTimeZoneId: userDetailsAuthService.usertimezoneid,
        twoLetterISOLanguageName: userDetailsAuthService.twoLetterISOLanguageName,
        isEmulatedUser,
        currentDomainId: userDetails.personInfo.currentDomainId
      },
      accessibleRcoreRoutes,
      securedRoutes,
      homePageUrl
    });
  }
  // Filter menus from sitemap data
  function filterMenus(data, quickLinks) {
    const filteredMenus = data.nodes.filter(
      (node) =>
        node.nodeKey === 'self_header' ||
        node.nodeKey === 'team_header' ||
        node.nodeKey === 'admin_header'
    );
    filteredMenus.forEach((menu) => {
      const node = menu;
      if (node.nodeKey === 'admin_header') {
        if (quickLinks && quickLinks.administration) {
          setQuickLinks(node, quickLinks.administration, 'administration_quicklinks');
        }
        // Compute max depth for the administration sitemap node
        node.maxDepthLevel = getMaxDepthOfTree(node, 'childNodes');
        node.maxDepthLevel = node.maxDepthLevel < 4 ? 4 : node.maxDepthLevel;
        node.childNodes.unshift({
          nodeKey: node.nodeKey,
          url: node.url,
          name: t('RCORE_TOPNAV_MENU_ADMINISTRATION_OVERVIEW'),
          rpKey: node.rpKey,
          childNodes: []
        });
      } else {
        node.showAdditionalAction = false;
        node.actionName = '';
        if (node.nodeKey === 'team_header' && quickLinks && quickLinks.myTeam) {
          setQuickLinks(node, quickLinks.myTeam, 'myteam_quicklinks');
        } else if (quickLinks && quickLinks.self) {
          setQuickLinks(node, quickLinks.self, 'self_quicklinks');
        }

        // filter profile nodes from self menu
        if (node.nodeKey === 'self_header' && node.childNodes.length > 0) {
          coreNodes.forEach((key) => {
            const nodeIndex = node.childNodes.findIndex((n) => n.nodeKey === key);
            if (nodeIndex !== -1) {
              node.childNodes.splice(nodeIndex, 1);
            }
          });
        }
        // Compute max depth for each of the sitemap node
        node.maxDepthLevel = getMaxDepthOfTree(node, 'childNodes');
        node.maxDepthLevel = node.maxDepthLevel < 2 ? 2 : node.maxDepthLevel;
      }
      if (node.childNodes.length === 0) {
        filteredMenus.splice(filteredMenus.indexOf(node), 1);
      }
    });
    setMenusData(filteredMenus);
    const shoppingCartNode = data.nodes.filter((node) => node.nodeKey === 'cart_header');
    if (shoppingCartNode.length > 0) setShoppingCartMenu(shoppingCartNode[0]);

    const helpNodes = data.nodes.filter((node) => node.nodeKey === 'help_header');
    if (helpNodes.length > 0) setHelpMenu(helpNodes[0]);

    const myGuideNodes = data.nodes.filter((node) => node.nodeKey === 'Inapp_guide_header');
    if (myGuideNodes.length > 0) setMyGuideMenu(myGuideNodes[0]);

    const notificationsNodes = data.nodes.filter(
      (node) => node.nodeKey === 'Inapp_notification_header'
    );
    if (notificationsNodes.length > 0) setNotificationsMenu(notificationsNodes[0]);

    const domainNodes = data.nodes.filter((node) => node.nodeKey === 'Administrator_SwitchDomain');
    if (domainNodes.length > 0) setDomainSwitchNode(domainNodes[0]);

    const searchNodes = data.nodes.filter((node) => node.nodeKey === 'search_header');
    if (searchNodes.length > 0) {
      setSearchUrl(searchNodes[0].url);
    }
  }

  function setQuickLinks(menuNodeList, configuredQuickLinks, menuQuickLinksNodeKey) {
    const configuredQuickLinkNodes = [];
    if (configuredQuickLinks.menuItems && configuredQuickLinks.menuItems.length > 0) {
      configuredQuickLinks.menuItems.forEach((quickLink) => {
        configuredQuickLinkNodes.push(
          findValueDeep(
            menuNodeList,
            (child) => {
              if (child.id === quickLink.masterSitemapId) {
                return child;
              }
            },
            { childrenPath: 'childNodes' }
          )
        );
      });
    }
    const availableQuickLinkNodes = configuredQuickLinkNodes.filter((item) => item);

    if (availableQuickLinkNodes.length > 0) {
      const menuQuickLinksNode = {
        nodeKey: menuQuickLinksNodeKey,
        name: t('RCORE_TOPNAV_MENU_QUICKLINKS_MENUITEM'),
        childNodes: availableQuickLinkNodes,
        expandQuickLinksByDefault: configuredQuickLinks.expandQuickLinksByDefault
      };
      menuNodeList.childNodes.unshift(menuQuickLinksNode);
      // eslint-disable-next-line no-param-reassign
      menuNodeList.expandQuickLinksNodeKey = configuredQuickLinks.expandQuickLinksByDefault
        ? menuQuickLinksNodeKey
        : null;
    }
  }

  function updateControlPanelMenus(menuNodeList, controlPanelMenuItems) {
    const configuredControlPanelNodes = [];
    if (
      !(controlPanelMenuItems && controlPanelMenuItems.length > 0) ||
      !(menuNodeList && menuNodeList.length > 0)
    ) {
      return null;
    }
    controlPanelMenuItems.forEach((menuItem) => {
      menuNodeList.some((node) => {
        const ControlPanelitem = findValueDeep(
          node,
          (child) => {
            if (menuItem.definition.type === 4) {
              if (child.id === -menuItem.definition.objectId) {
                return child;
              }
            } else if (child.id === menuItem.definition.node.id) {
              return child;
            }
          },
          { childrenPath: 'childNodes' }
        );

        if (ControlPanelitem) {
          ControlPanelitem.iconName = menuItem.definition.iconName;
          ControlPanelitem.actionType = menuItem.definition.actionType;
          ControlPanelitem.url = GetProcessedNodeUrl(ControlPanelitem);
          if (menuItem.definition.iconSource === 1) {
            ControlPanelitem.iconType = 'MaterialIcon';
          } else if (menuItem.definition.iconSource === 2) {
            ControlPanelitem.iconType = 'IcomoonIcon';
          }
          configuredControlPanelNodes.push(ControlPanelitem);
          return true;
        }
        return false;
      });
    });
    return configuredControlPanelNodes;
  }

  const handleIntegrationHomeBtn = () => {
    window.location.assign('/rcore/mylearning?im=1');
  };

  const handleControlPanelItem = (SelectedNode) => {
    if (SelectedNode) {
      setSelectedMenuKey(SelectedNode.nodeKey);
    }
  };

  if (!isLoaded || !coreReady) {
    if (!coreReady && headerContext.coreFrameLoaded) setCoreReady(true);
    return <PageLoader show />;
  }

  if (errorMessage !== '') {
    return <div>{errorMessage}</div>;
  }

  return (
    <>
      {!CheckIntegrationSetting(IntegrationElement.ShowHomeButton, integrationContext.mode) && (
        <div>
          <IntegrationHomeBtn onClick={handleIntegrationHomeBtn} size="small" variant="primary">
            {t('TRANSLATIONS_RCORE_COMMON:RCORE_HOME_TEXT')}
          </IntegrationHomeBtn>
        </div>
      )}
      {CheckIntegrationSetting(IntegrationElement.HideTopBar, integrationContext.mode) && (
        <HeaderSection className="header no-print">
          <AppMenuHeader
            logoClickUrl={homeUrl}
            menusData={menusData}
            userData={userData}
            profileMenus={profileMenus}
            helpMenu={helpMenu}
            myGuideMenu={myGuideMenu}
            notificationsMenu={notificationsMenu}
            domainSwitchNode={domainSwitchNode}
            searchTypes={searchTypes}
            searchUrl={searchUrl}
            shoppingCartMenu={shoppingCartMenu}
            onNavigationCallback={handleControlPanelItem}
          />
        </HeaderSection>
      )}
      {emulatedUser && emulatedUser !== '' && exitEmulationUrl && exitEmulationUrl !== '' && (
        <EmulationStrip emulatedUser={emulatedUser} exitEmulationUrl={exitEmulationUrl} />
      )}
      {CheckIntegrationSetting(IntegrationElement.HideControlPanel, integrationContext.mode) && (
        <ExpandedControlPanelContainer
          controlPanelItems={controlPanelItems}
          highlightControlPanelItem={selectedMenuKey}
          setHighlightCPItem={setSelectedMenuKey}
        />
      )}
    </>
  );
}

SitemapProvider.propTypes = {
  /**
   Emulated user name
  * */
  emulatedUser: PropTypes.string
};

const defaultProps = {
  emulatedUser: ''
};

export default SitemapProvider;
